import { createContext as e, useCallback as t, useContext as n, useEffect as r, useMemo as i, useRef as a, useState as o } from "react";
import { jsx as s, jsxs as c } from "react/jsx-runtime";
import { useMutation as l, useQuery as u, useQueryClient as ee } from "@tanstack/react-query";
import d from "axios";
import { APP_CHAT_RICH_PART_TYPES as f, CHAT_UPLOAD_MAX_BYTES as p, MAX_CHAT_ATTACHMENTS_PER_MESSAGE as m, chatWithAppContract as te, getProcessChainTreeContract as h, getProcessLayoutContract as g, isChatUploadMediaType as _, isReservedChatMessageId as ne, listExternalContactsContract as v, listOrgFunctionsContract as y, listOrgMembersContract as b, listSystemsContract as x, loadAppChatHistoryContract as re, presignAppChatUploadContract as S, settledToolPartFields as C, startAppChatContract as ie, transcribeAudioContract as w } from "@bliks-io/contracts";
import { CHAT_UPLOAD_ACCEPT as T, audioBlobFilename as E, buildAssigneeLookup as D, uploadToPresignedPost as O, useChatAttachmentDrafts as k } from "@bliks-io/ui";
import { useChat as ae } from "@ai-sdk/react";
import { DefaultChatTransport as oe, getToolName as A, isToolUIPart as j } from "ai";
//#region src/react/context.tsx
var M = e(null);
function N({ value: e, children: t }) {
	return /* @__PURE__ */ s(M.Provider, {
		value: e,
		children: t
	});
}
function P() {
	let e = n(M);
	if (!e) throw Error("useAppContext must be used within an AppContextProvider");
	return e;
}
//#endregion
//#region src/react/hooks/use-api-client.ts
function F() {
	let { apiBaseUrl: e, accessToken: t } = P();
	return i(() => d.create({
		baseURL: e,
		headers: { Authorization: `Bearer ${t}` }
	}), [e, t]);
}
//#endregion
//#region src/react/hooks/use-data.ts
function I(e) {
	let t = F();
	return u({
		...e,
		queryKey: ["processes", "list"],
		queryFn: async ({ signal: e }) => {
			let { data: n } = await t.get("/api/v2/process", { signal: e });
			return n.processes ?? [];
		}
	});
}
function L(e, t) {
	return {
		queryKey: [
			"processes",
			"detail",
			t
		],
		queryFn: async ({ signal: n }) => {
			let { data: r } = await e.get(`/api/v2/process/${t}`, { signal: n });
			return r;
		}
	};
}
function R(e, t, n) {
	return u({
		...n,
		...e,
		enabled: (n?.enabled ?? !0) && !!t
	});
}
function z(e, t) {
	return R(L(F(), e), e, t);
}
function B() {
	let e = F();
	return u({
		queryKey: ["process-chains", "tree"],
		queryFn: async ({ signal: t }) => {
			let { data: n } = await e.get(h.path, { signal: t });
			return n;
		}
	});
}
//#endregion
//#region src/react/hooks/use-process-layout.ts
function V(e, t = "published", n) {
	let r = F();
	return u({
		...n,
		queryKey: [
			"process",
			"layout",
			e,
			t
		],
		queryFn: async ({ signal: n }) => {
			let { data: i } = await r.get(g.path.replace(":processId", e), {
				params: { scope: t },
				signal: n
			});
			return {
				savedPositions: [...i.stepPositions.map((e) => ({
					stepId: String(e.stableStepId),
					x: e.x,
					y: e.y
				})), ...i.generatedNodePositions.map((e) => ({
					stepId: e.nodeId,
					x: e.x,
					y: e.y
				}))],
				swimlaneOrder: i.swimlaneOrder,
				laneColors: i.laneColors
			};
		},
		enabled: (n?.enabled ?? !0) && !!e
	});
}
//#endregion
//#region src/react/hooks/use-assignee-lookup.ts
function H() {
	let e = F();
	return u({
		queryKey: ["org-lookup", "org-member"],
		queryFn: async ({ signal: t }) => {
			let { data: n } = await e.get(`${b.path}?includeDeleted=true`, { signal: t });
			return n;
		}
	});
}
function U() {
	let e = F();
	return u({
		queryKey: ["org-lookup", "org-function"],
		queryFn: async ({ signal: t }) => {
			let { data: n } = await e.get(`${y.path}?includeDeleted=true`, { signal: t });
			return n.data;
		}
	});
}
function W() {
	let e = F();
	return u({
		queryKey: ["org-lookup", "system"],
		queryFn: async ({ signal: t }) => {
			let { data: n } = await e.get(`${x.path}?includeDeleted=true`, { signal: t });
			return n.data;
		}
	});
}
function G() {
	let e = F();
	return u({
		queryKey: ["org-lookup", "external-contact"],
		queryFn: async ({ signal: t }) => {
			let { data: n } = await e.get(`${v.path}?includeDeleted=true`, { signal: t });
			return n.data;
		}
	});
}
var K = Object.freeze([]);
function se() {
	let e = H(), t = U(), n = W(), r = G();
	return i(() => D({
		users: e.data ?? K,
		orgFunctions: t.data ?? K,
		systems: n.data ?? K,
		externalContacts: r.data ?? K
	}), [
		e.data,
		t.data,
		n.data,
		r.data
	]);
}
//#endregion
//#region src/react/hooks/use-app-function.ts
function ce(e, t) {
	let n = F(), { app: r } = P();
	return l({
		...t,
		mutationFn: async (t) => {
			let { data: i } = await n.post(`/api/v2/apps/${r.id}/fn/${e}`, t);
			return i;
		}
	});
}
function q(e, t) {
	return [
		"apps",
		e,
		"fn",
		t
	];
}
function le(e, t, n) {
	let r = F(), { app: i } = P();
	return u({
		...n,
		queryKey: [...q(i.id, e), t ?? {}],
		queryFn: async ({ signal: n }) => {
			let { data: a } = await r.post(`/api/v2/apps/${i.id}/fn/${e}`, t ?? {}, { signal: n });
			return a.result;
		}
	});
}
function ue(e) {
	let t = F(), { app: n } = P();
	return l({ mutationFn: async (r) => {
		let { data: i } = await t.post(`/api/v2/apps/${n.id}/fn/${e}/async`, r);
		return i;
	} });
}
//#endregion
//#region src/react/hooks/use-transcribe-audio.ts
function de() {
	let e = F();
	return l({ mutationFn: async (t) => {
		let n = new FormData();
		n.append("audio", t, E(t));
		let { data: r } = await e.post(w.path, n, { timeout: 6e4 });
		return r.text;
	} });
}
//#endregion
//#region src/react/hooks/use-app-storage.ts
function J(e, t) {
	return [
		"apps",
		e,
		"storage",
		t
	];
}
function Y(e, t, n, r) {
	return {
		queryKey: [...J(t, n), r ?? {}],
		queryFn: async ({ signal: i }) => {
			let { data: a } = await e.get(`/api/v2/apps/${t}/storage/${n}`, {
				params: r,
				signal: i
			});
			return a.data;
		}
	};
}
function fe(e, t, n) {
	let r = F(), { app: i } = P();
	return u({
		...n,
		...Y(r, i.id, e, t)
	});
}
function X(e, t, n, r) {
	return {
		queryKey: [...J(t, n), r],
		queryFn: async ({ signal: i }) => {
			let { data: a } = await e.get(`/api/v2/apps/${t}/storage/${n}/${r}`, { signal: i });
			return a.data;
		}
	};
}
function pe(e, t, n) {
	let r = F(), { app: i } = P();
	return u({
		...n,
		...X(r, i.id, e, t),
		enabled: (n?.enabled ?? !0) && !!t
	});
}
//#endregion
//#region src/react/lib/app-chat-path.ts
function Z(e, t) {
	let n = e.replace(":appId", encodeURIComponent(t.appId)).replace(":kind", encodeURIComponent(t.kind));
	return t.sessionId !== void 0 && (n = n.replace(":sessionId", encodeURIComponent(t.sessionId))), n;
}
//#endregion
//#region src/react/lib/app-chat-messages.ts
function me(e) {
	return e.type === "text" ? {
		type: "text",
		text: e.text
	} : e.type === "file" ? {
		type: "file",
		mediaType: e.mediaType,
		filename: e.filename,
		url: ""
	} : e.type === "tool-app" ? {
		type: `tool-${e.toolName}`,
		toolCallId: e.toolCallId,
		input: e.input,
		...C(e)
	} : e;
}
function he(e) {
	return e.parts && e.parts.length > 0 ? e.parts.map(me) : [{
		type: "text",
		text: e.content ?? ""
	}];
}
function Q(e) {
	return e.map((e, t) => ({
		id: e.id ?? `history-${t}`,
		role: e.role,
		parts: he(e),
		metadata: {
			langfuseTraceId: e.langfuseTraceId,
			entityRefs: e.entityRefs,
			aiProvenance: e.aiProvenance,
			userFeedback: e.userFeedback,
			createdAt: e.createdAt
		}
	}));
}
function ge(e) {
	let t = /* @__PURE__ */ new Map();
	for (let n of e) {
		if (!n.id || n.role !== "user") continue;
		let e = (n.parts ?? []).flatMap((e) => e.type === "file" ? [{
			key: e.storageKey,
			filename: e.filename,
			mediaType: e.mediaType
		}] : []);
		t.set(n.id, e);
	}
	return t;
}
function _e(e) {
	switch (e) {
		case "output-available": return "output-available";
		case "output-error":
		case "output-denied": return "output-error";
		case "input-streaming": return "input-streaming";
		default: return "input-available";
	}
}
var ve = /* @__PURE__ */ new Set(["refused", "conflict"]);
function ye(e) {
	if (typeof e != "object" || !e) return !1;
	let { status: t } = e;
	return typeof t == "string" && ve.has(t);
}
var be = new Set(f);
function xe(e) {
	return e.type === "text" ? [{
		type: "text",
		text: e.text
	}] : e.type === "file" ? [{
		type: "file",
		filename: e.filename,
		mediaType: _(e.mediaType) ? e.mediaType : void 0
	}] : j(e) ? be.has(e.type) ? [{
		type: e.type,
		toolCallId: e.toolCallId,
		state: _e(e.state),
		...e.state === "output-available" ? { output: e.output } : {}
	}] : [{
		type: "tool",
		toolName: A(e),
		toolCallId: e.toolCallId,
		state: _e(e.state),
		...e.input === void 0 ? {} : { input: e.input },
		...ye(e.output) ? { rejected: !0 } : {}
	}] : [];
}
var Se = /* @__PURE__ */ new WeakMap();
function Ce(e) {
	let t = [];
	for (let n of e) {
		if (n.role !== "user" && n.role !== "assistant") continue;
		let e = Se.get(n);
		if (e) {
			t.push(e);
			continue;
		}
		let r = we(n.metadata), i = {
			id: n.id,
			role: n.role,
			parts: n.parts.flatMap(xe),
			...r ? { metadata: r } : {}
		};
		Se.set(n, i), t.push(i);
	}
	return t;
}
function we(e) {
	if (typeof e != "object" || !e) return;
	let { entityRefs: t, langfuseTraceId: n, aiProvenance: r, userFeedback: i, createdAt: a } = e, o = {
		...t?.length ? { entityRefs: t } : {},
		...n ? { langfuseTraceId: n } : {},
		...r ? { aiProvenance: r } : {},
		...i ? { userFeedback: i } : {},
		...a ? { createdAt: a } : {}
	};
	return Object.keys(o).length > 0 ? o : void 0;
}
//#endregion
//#region src/react/hooks/use-app-chat.ts
var Te = 50, $ = {
	session: (e, t, n) => [
		"app-chat",
		e,
		t,
		"session",
		n
	],
	history: (e, t, n) => [
		"app-chat",
		e,
		t,
		"history",
		n
	]
}, Ee = 409;
function De(e) {
	if (!e) return !1;
	try {
		let t = JSON.parse(e.message);
		return typeof t == "object" && !!t && t.statusCode === Ee;
	} catch {
		return !1;
	}
}
function Oe({ kind: e, subjectKey: n, sessionId: s, enabled: c = !0, onSessionCreated: l, onError: d }) {
	let { apiBaseUrl: f, accessToken: p, app: m } = P(), h = F(), g = ee(), _ = m.id, v = n === void 0, y = u({
		queryKey: $.session(_, e, n ?? ""),
		queryFn: async () => {
			let t = { subjectKey: n }, { data: r } = await h.post(Z(ie.path, {
				appId: _,
				kind: e
			}), t);
			return r;
		},
		enabled: c && n !== void 0 && n.length > 0,
		staleTime: Infinity,
		retry: !1
	}), [b, x] = o(void 0), S = s ?? y.data?.sessionId ?? b, C = a(s !== void 0);
	r(function() {
		C.current && s === void 0 && x(void 0), C.current = s !== void 0;
	}, [s]);
	let w = a(S);
	w.current = S;
	let T = u({
		queryKey: $.history(_, e, S),
		queryFn: async ({ signal: t }) => {
			let { data: n } = await h.get(Z(re.path, {
				appId: _,
				kind: e,
				sessionId: S
			}), { signal: t });
			return n;
		},
		enabled: !!S,
		refetchOnWindowFocus: !1
	}), E = T.data?.messages, D = i(() => E ? Q(E) : [], [E]), O = a([]), k = a(/* @__PURE__ */ new Map());
	k.current = i(() => E ? ge(E) : /* @__PURE__ */ new Map(), [E]);
	let A = i(() => new oe({
		api: `${f}${Z(te.path, {
			appId: _,
			kind: e
		})}`,
		headers: () => ({ Authorization: `Bearer ${p}` }),
		prepareSendMessagesRequest: ({ messages: e }) => ({ body: ke({
			messages: e,
			sessionId: w.current,
			composing: O.current,
			stored: k.current
		}) })
	}), [
		f,
		p,
		_,
		e
	]), j = ae({
		id: s ?? y.data?.sessionId ?? `app-chat:${_}:${e}:${n ?? "new"}`,
		messages: D,
		transport: A,
		throttle: 50,
		onFinish: ({ message: t, isError: n }) => {
			let r = t.metadata, i = n ? void 0 : r?.sessionId;
			i && !w.current && (w.current = i, x(i), l?.(i)), g.invalidateQueries({ queryKey: $.history(_, e, w.current) });
		},
		...d ? { onError: d } : {}
	}), { sendMessage: M, setMessages: N } = j, I = j.status === "streaming" || j.status === "submitted", L = a(!1);
	I || (L.current = !1);
	let R = a(void 0);
	r(function() {
		I || T.isFetching || !T.data || R.current === T.data || (R.current = T.data, N(Q(T.data.messages)));
	}, [
		S,
		T.data,
		T.isFetching,
		I,
		N
	]);
	let z = t(({ text: e, attachments: t = [] }) => !v && !w.current || !e.trim() || L.current ? !1 : (L.current = !0, O.current = t, M({
		text: e,
		files: t.map((e) => ({
			type: "file",
			mediaType: e.mediaType,
			filename: e.filename,
			url: ""
		})),
		metadata: { createdAt: (/* @__PURE__ */ new Date()).toISOString() }
	}), !0), [M]), B = i(() => [...j.messages].reverse().find((e) => e.role === "user")?.id, [j.messages]), V = B !== void 0 && !ne(B), H = i(() => Ce(j.messages), [j.messages]), U = t((e) => (v || !!S) && !I && !L.current && e.trim().length > 0, [
		v,
		S,
		I
	]);
	return {
		messages: H,
		sessionId: S,
		subjectRef: y.data?.subjectRef ?? T.data?.subjectRef,
		overlayPromptKey: y.data?.overlayPromptKey,
		isStarting: y.isPending && y.fetchStatus !== "idle",
		isHistoryLoading: T.isPending && T.fetchStatus !== "idle",
		isLoading: I,
		canSend: U,
		status: j.status,
		error: y.error ?? T.error ?? j.error ?? void 0,
		turnError: j.error ?? void 0,
		sendMessage: z,
		retry: V ? () => void j.regenerate() : void 0
	};
}
function ke({ messages: e, sessionId: t, composing: n, stored: r }) {
	let i = e[e.length - 1], a = i?.parts?.find((e) => e.type === "text"), o = a && "text" in a ? a.text : "", s = i ? r.get(i.id) ?? n : n;
	return {
		sessionId: t,
		message: {
			id: i?.id,
			content: o
		},
		...s.length > 0 ? { attachments: s } : {}
	};
}
//#endregion
//#region src/react/hooks/use-app-chat-attachments.ts
var Ae = Math.floor(p / 1048576), je = {
	reason: "upload-failed",
	values: {}
}, Me = {
	reason: "unsupported-type",
	values: {}
};
function Ne(e, t) {
	return t >= m ? {
		reason: "too-many",
		values: { max: m }
	} : e.size > p ? {
		reason: "too-large",
		values: { maxMegabytes: Ae }
	} : null;
}
function Pe({ kind: e, sessionId: t }) {
	let n = F(), { app: r } = P(), i = r.id;
	return {
		...k({
			reject: Ne,
			upload: async (t, { signal: r, onProgress: a, mediaType: o }) => {
				let s = {
					filename: t.name,
					mediaType: o,
					size: t.size
				}, { data: c } = await n.post(Z(S.path, {
					appId: i,
					kind: e
				}), s);
				return await O(c.url, c.fields, t, {
					signal: r,
					onProgress: a
				}), { key: c.key };
			},
			uploadFailedRejection: je,
			unsupportedTypeRejection: Me,
			...t ? { sessionKey: t } : {}
		}),
		accept: T
	};
}
//#endregion
//#region src/react/lib/xlsx-ai-provenance.ts
function Fe(e) {
	let t = { aiGenerated: "true" };
	return e?.foundationModel && (t.foundationModel = e.foundationModel), e?.promptName && (t.promptName = e.promptName), e?.promptVersion !== void 0 && (t.promptVersion = String(e.promptVersion)), t;
}
function Ie(e, t) {
	let n = e?.foundationModel, r = e?.promptVersion;
	return n && r !== void 0 ? t("excelAiNoteModelPrompt", {
		model: n,
		version: r
	}) : n ? t("excelAiNoteModel", { model: n }) : r === void 0 ? t("excelAiNote") : t("excelAiNotePrompt", { version: r });
}
function Le(e, { title: t, provenance: n }) {
	e.Props = {
		Title: t,
		Author: "BLIKS IO",
		Keywords: "AI-generated"
	}, e.Custprops = Fe(n);
}
//#endregion
//#region src/react/components/app-page.tsx
function Re({ title: e, headerRight: t, children: n }) {
	return /* @__PURE__ */ c("div", {
		className: "flex flex-col h-full",
		children: [/* @__PURE__ */ c("div", {
			className: "flex items-center justify-between gap-4 px-6 py-4 border-b border-border",
			children: [/* @__PURE__ */ s("h1", {
				className: "text-2xl font-semibold",
				children: e
			}), t]
		}), /* @__PURE__ */ s("div", {
			className: "flex-1 overflow-auto p-6",
			children: n
		})]
	});
}
//#endregion
//#region src/react/components/app-skeleton.tsx
function ze() {
	return /* @__PURE__ */ c("div", {
		className: "flex flex-col h-full animate-pulse",
		children: [/* @__PURE__ */ s("div", {
			className: "px-6 py-4 border-b border-border",
			children: /* @__PURE__ */ s("div", { className: "h-8 w-48 bg-muted rounded" })
		}), /* @__PURE__ */ c("div", {
			className: "flex-1 p-6 space-y-4",
			children: [
				/* @__PURE__ */ s("div", { className: "h-4 w-full bg-muted rounded" }),
				/* @__PURE__ */ s("div", { className: "h-4 w-3/4 bg-muted rounded" }),
				/* @__PURE__ */ s("div", { className: "h-32 w-full bg-muted rounded" })
			]
		})]
	});
}
//#endregion
export { Te as APP_CHAT_STREAM_THROTTLE_MS, N as AppContextProvider, Re as AppPage, ze as AppSkeleton, Q as appChatHistoryToUIMessages, $ as appChatQueryKeys, J as appCollectionQueryKey, Y as appCollectionQueryOptions, X as appDocumentQueryOptions, q as appFunctionQueryKey, Ie as buildAiNote, Z as buildAppChatPath, ge as collectAppChatHistoryAttachments, De as isAppChatSessionBusyError, Le as markWorkbookAiGenerated, L as processQueryOptions, Ce as toAiChatPanelMessages, F as useAppApiClient, Oe as useAppChat, Pe as useAppChatAttachments, fe as useAppCollection, P as useAppContext, pe as useAppDocument, ce as useAppFunction, le as useAppFunctionQuery, se as useAssigneeLookup, ue as useAsyncAppFunction, G as useExternalContactsForLookup, U as useOrgFunctionsForLookup, H as useOrgMembersForLookup, z as useProcess, B as useProcessChainTree, V as useProcessLayout, I as useProcesses, W as useSystemsForLookup, de as useTranscribeAudio };
